/**
 * feat-299 — `<InlineEditable>`: one click-to-edit text run on `/quiz-review`
 * (spec §3, design NOTES §1–§6).
 *
 * rest → hover/focus affordance → open (an autosizing `<textarea>`) → saving →
 * saved, or error-with-retry. The textarea is used for SINGLE-line fields too: an
 * `<input>` cannot wrap, and a two-line stem in one scrolls sideways and hides
 * its own text (NOTES §0). Single- vs multi-line is KEY HANDLING only:
 *
 *   |              | single-line | multi-line |
 *   | Enter        | save        | newline    |
 *   | ⌘/Ctrl+Enter | save        | save       |
 *   | Esc          | cancel      | cancel     |
 *   | blur         | save        | save       |
 *
 * Unchanged text (trimmed; newlines folded to spaces on single-line fields)
 * sends NO request. A required field cannot be saved empty: nothing is sent and
 * the editor stays open with an inline message (AT-7).
 *
 * On a failed save the run shows the LAST SERVER VALUE (`value`), and the typed
 * text is kept in an error box with Try again · Edit · Discard (AT-8).
 *
 * This component knows nothing about curriculum: `onSave` is the whole contract.
 * It renders English only; a translated cell never mounts one (spec §4).
 */
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ApiError } from '../../../utils/api';
import AUTO_SAVE_COPY from '../curriculum/autoSaveCopy';
import { SAVE_SETTLE_DELAY_MS, SaveSpinner } from '../curriculum/SaveStatus';
import { QUIZ_REVIEW_EDIT_COPY as COPY } from './editCopy';
import { useEditCoordinator, useEditLineReporter } from './editCoordinator';

// ------------------------------------------------------------ classes (NOTES §2–§5)

export const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40';

const RUN =
  'rounded-[4px] -mx-1 px-1 box-decoration-clone cursor-text ' +
  'transition-colors duration-100 motion-reduce:transition-none ' +
  'hover:bg-black/[0.045] ' +
  'hover:underline hover:decoration-dotted hover:decoration-1 hover:decoration-inkMuted/70 hover:underline-offset-[3px] ' +
  FOCUS;

const BLOCK =
  'block rounded-md -mx-1.5 px-1.5 -my-1 py-1 cursor-text ' +
  'transition-colors duration-100 motion-reduce:transition-none ' +
  'hover:bg-black/[0.035] hover:ring-1 hover:ring-black/10 ' +
  FOCUS;

const PLACEHOLDER =
  'text-inkMuted/80 italic underline decoration-dotted decoration-1 decoration-inkMuted/50 underline-offset-[3px]';

const EDITOR =
  'block w-full min-w-0 resize-none overflow-hidden rounded-md bg-white ' +
  '-mx-1 px-1 -my-0.5 py-0.5 not-italic text-ink shadow-sm outline-none';
const RING_OK = 'ring-2 ring-coral/40';
const RING_INVALID = 'ring-2 ring-attention-line';

/** The page's one key-hint style (feat-300's composer imports it too). */
export const KBD =
  'font-sans text-[10px] font-bold leading-none px-1 py-[3px] rounded bg-white ring-1 ring-black/10 text-ink';
const BTN_SAVE = `h-7 px-3 rounded-full bg-white ring-1 ring-black/10 text-xs font-semibold text-ink hover:bg-cream-dark ${FOCUS}`;
const BTN_CANCEL = `h-7 px-2 rounded-full text-xs font-semibold text-inkMuted hover:text-ink hover:bg-cream-dark ${FOCUS}`;

export const STATUS =
  'ml-1.5 inline-flex items-center gap-1 align-middle text-xs leading-none font-normal not-italic text-inkMuted';

export const ERROR_BOX =
  'mt-1 rounded-lg bg-danger-surface border border-danger-line/40 px-2.5 py-1.5 ' +
  'text-xs leading-snug font-normal not-italic text-danger-text';
export const ERRBTN = `font-semibold underline underline-offset-2 hover:no-underline rounded ${FOCUS}`;

/** The one page-level `sr-only` node every run points `aria-describedby` at. */
export const SR_OPEN_HINT_ID = 'quiz-review-inline-edit-hint';

export const SrOpenHint: React.FC = () => (
  <span id={SR_OPEN_HINT_ID} className="sr-only">
    {COPY.srOpenHint}
  </span>
);

const isMac = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

/** Normalise for both the "unchanged?" test and the value sent. */
export function normalizeDraft(raw: string, multiline: boolean): string {
  const folded = multiline ? raw : raw.replace(/\s*\n\s*/g, ' ');
  return folded.trim();
}

/** The character offset of a viewport point inside `root`'s text, or null. */
function caretOffsetAt(root: HTMLElement, x: number, y: number): number | null {
  const doc = root.ownerDocument as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let node: Node | null = null;
  let offset = 0;
  if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(x, y);
    if (pos) {
      node = pos.offsetNode;
      offset = pos.offset;
    }
  } else if (typeof doc.caretRangeFromPoint === 'function') {
    const r = doc.caretRangeFromPoint(x, y);
    if (r) {
      node = r.startContainer;
      offset = r.startOffset;
    }
  }
  if (!node || !root.contains(node)) return null;
  try {
    const range = doc.createRange();
    range.setStart(root, 0);
    range.setEnd(node, offset);
    return range.toString().length;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------ the component

export interface InlineEditableProps {
  /** The last server value. `''` for an empty field. */
  value: string;
  /** Wire field name — used for test ids. */
  field: string;
  /** Overrides `inline-{field}` (criteria: `inline-criteria-{idx}-description`). */
  testId?: string;
  /** Human label, used in messages: "choice B", "the question". */
  label: string;
  multiline?: boolean;
  required?: boolean;
  /** Shown, muted, when the value is empty. Optional fields (NOTES §6), plus the
   *  required long fields that have no legacy fallback text (QA-F2) — those
   *  also pass `required`, so an empty save is still blocked (AT-7). */
  placeholder?: string;
  /** Required fields with legacy-empty data: today's fallback text, made clickable. */
  fallback?: React.ReactNode;
  display?: 'inline' | 'block';
  /** Custom resting render, e.g. `MarkdownPreview` for the reading body. */
  render?: (value: string) => React.ReactNode;
  /** Extra classes on the resting run (tone, weight). */
  className?: string;
  /** Rendered under the hint row while open (the reading body's markdown hint). */
  editorNote?: React.ReactNode;
  /** Told when the editor opens/closes (the reading body drops its clamp). */
  onOpenChange?: (open: boolean) => void;
  onSave: (next: string) => Promise<void>;
  disabled?: boolean;
}

type Phase = 'rest' | 'open' | 'saving';

interface SaveError {
  draft: string;
  expired: boolean;
}

const InlineEditable: React.FC<InlineEditableProps> = ({
  value,
  field,
  testId,
  label,
  multiline = false,
  required = false,
  placeholder,
  fallback,
  display = 'inline',
  render,
  className = '',
  editorNote,
  onOpenChange,
  onSave,
  disabled = false,
}) => {
  const [phase, setPhase] = useState<Phase>('rest');
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState<SaveError | null>(null);
  const [saved, setSaved] = useState(false);
  /** The text being saved — shown in the run while the write is in flight. */
  const [optimistic, setOptimistic] = useState<string | null>(null);

  const msgId = useId();
  const runRef = useRef<HTMLElement | null>(null);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const caretRef = useRef<number | null>(null);
  const focusRunRef = useRef(false);
  const mountedRef = useRef(true);
  const tokenRef = useRef<object>({});
  // Latest-value refs: `commit` is handed to the coordinator and must read the
  // current draft/phase, not the ones from the render that opened the editor.
  const phaseRef = useRef<Phase>('rest');
  phaseRef.current = phase;
  const draftRef = useRef('');
  draftRef.current = draft;
  const valueRef = useRef(value);
  valueRef.current = value;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const coordinator = useEditCoordinator();
  const reportLine = useEditLineReporter();
  const testIdBase = testId ?? `inline-${field}`;

  useEffect(() => {
    mountedRef.current = true;
    const token = tokenRef.current;
    return () => {
      mountedRef.current = false;
      coordinator?.release(token);
    };
  }, [coordinator]);

  const isOpen = phase === 'open';
  useEffect(() => {
    reportLine?.(isOpen);
    onOpenChange?.(isOpen);
  }, [isOpen, reportLine, onOpenChange]);

  // "✓ Saved" clears itself after the settle delay (NOTES §4).
  useEffect(() => {
    if (!saved) return undefined;
    const t = window.setTimeout(() => setSaved(false), SAVE_SETTLE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [saved]);

  // Return focus to the run after Enter / Esc / the buttons — not after a blur,
  // where focus stays wherever the reviewer put it.
  useEffect(() => {
    if (phase !== 'open' && focusRunRef.current) {
      focusRunRef.current = false;
      runRef.current?.focus();
    }
  }, [phase]);

  // --- open: focus, caret at the click point, autosize ----------------------------
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || phase !== 'open') return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, phase]);

  useLayoutEffect(() => {
    if (phase !== 'open') return;
    const el = editorRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    const at = caretRef.current;
    const pos = at == null ? el.value.length : Math.min(at, el.value.length);
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      /* jsdom / exotic inputs */
    }
    caretRef.current = null;
    // Only on the transition into `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // --- the save itself -------------------------------------------------------------------
  const runSave = useCallback(
    async (next: string, typed: string) => {
      setPhase('saving');
      setOptimistic(next);
      setError(null);
      setInvalid(false);
      coordinator?.release(tokenRef.current);
      try {
        await onSaveRef.current(next);
        if (!mountedRef.current) return;
        setOptimistic(null);
        setPhase('rest');
        setSaved(true);
      } catch (e) {
        if (!mountedRef.current) return;
        setOptimistic(null);
        setPhase('rest');
        setSaved(false);
        // 401 only (NOTES §4): 403 and everything else get the generic line.
        setError({ draft: typed, expired: e instanceof ApiError && e.status === 401 });
      }
    },
    [coordinator],
  );

  /** Close the editor without saving. */
  const close = useCallback(
    (returnFocus: boolean) => {
      coordinator?.release(tokenRef.current);
      setInvalid(false);
      focusRunRef.current = returnFocus;
      setPhase('rest');
    },
    [coordinator],
  );

  /**
   * Commit the open editor: Enter, ⌘/Ctrl+Enter, Save, blur, or another field
   * opening. Idempotent — it acts only while this editor is still open.
   */
  const commit = useCallback(
    (returnFocus: boolean) => {
      if (phaseRef.current !== 'open') return;
      const typed = draftRef.current;
      const next = normalizeDraft(typed, multiline);
      if (next === normalizeDraft(valueRef.current ?? '', multiline)) {
        close(returnFocus);
        return;
      }
      if (required && next === '') {
        setInvalid(true);
        return;
      }
      focusRunRef.current = returnFocus;
      void runSave(next, typed);
    },
    [multiline, required, close, runSave],
  );

  const open = useCallback(
    (initial: string) => {
      if (disabled || phaseRef.current !== 'rest') return;
      setDraft(initial);
      setInvalid(false);
      setSaved(false);
      setPhase('open');
      coordinator?.claim(tokenRef.current, () => commit(false));
    },
    [disabled, coordinator, commit],
  );

  // --- resting run handlers ------------------------------------------------------------
  const onRunClick = (e: React.MouseEvent<HTMLElement>) => {
    if (phase !== 'rest' || disabled) return;
    // Dragging to select a phrase must not open the editor (NOTES §2).
    const sel = typeof window.getSelection === 'function' ? window.getSelection() : null;
    if (sel && !sel.isCollapsed) return;
    // A link inside a rendered body (reading markdown) keeps working.
    if ((e.target as HTMLElement).closest?.('a')) return;
    if (!render && runRef.current && value) {
      caretRef.current = caretOffsetAt(runRef.current, e.clientX, e.clientY);
    }
    open(value ?? '');
  };

  const onRunKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Space would scroll the page.
      open(value ?? '');
    }
  };

  // --- editor handlers ---------------------------------------------------------------------
  const onEditorKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key === 'Enter') {
      const modified = e.metaKey || e.ctrlKey;
      if (modified || !multiline) {
        e.preventDefault();
        commit(true);
      }
    }
  };

  const onEditorChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(e.target.value);
    if (invalid && e.target.value.trim() !== '') setInvalid(false);
  };

  // Clicking Save/Cancel must not blur the textarea first (NOTES §3), or Cancel
  // would trigger a blur-save before it ran.
  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  // --- render -------------------------------------------------------------------------------
  const shown = optimistic ?? value ?? '';
  const empty = shown === '';
  const block = display === 'block';
  const Tag = (block ? 'div' : 'span') as 'span';

  let restContent: React.ReactNode;
  if (empty && placeholder) restContent = placeholder;
  else if (empty && fallback !== undefined) restContent = fallback;
  else restContent = render ? render(shown) : shown;

  const saving = phase === 'saving';
  const status = saving ? (
    <span className={STATUS} aria-live="polite" data-testid={`inline-status-${field}`}>
      <SaveSpinner size={12} />
      {AUTO_SAVE_COPY.saving}
    </span>
  ) : saved ? (
    <span
      className={`${STATUS} transition-opacity duration-300`}
      aria-live="polite"
      data-testid={`inline-status-${field}`}
    >
      <span aria-hidden>✓</span> {AUTO_SAVE_COPY.saved}
    </span>
  ) : null;

  const errorBox = error && (
    <div role="alert" className={ERROR_BOX} data-testid={`inline-error-${field}`}>
      <span className="font-semibold">
        <span aria-hidden>⚠</span>{' '}
        {error.expired ? AUTO_SAVE_COPY.signInExpired(label) : AUTO_SAVE_COPY.couldNotSaveField(label)}
      </span>{' '}
      {COPY.yourTextIsStillHere}
      <div className="mt-1 border-l-2 border-danger-line/40 pl-2 text-ink break-words whitespace-pre-wrap line-clamp-3">
        {error.draft}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
        {error.expired && <span>{AUTO_SAVE_COPY.signInAgainThen}</span>}
        <button
          type="button"
          className={ERRBTN}
          onClick={() => {
            const typed = error.draft;
            const next = normalizeDraft(typed, multiline);
            if (required && next === '') {
              setError(null);
              open(typed);
              return;
            }
            void runSave(next, typed);
          }}
        >
          {AUTO_SAVE_COPY.tryAgain}
        </button>
        <button
          type="button"
          className={ERRBTN}
          onClick={() => {
            const typed = error.draft;
            setError(null);
            open(typed);
          }}
        >
          {COPY.edit}
        </button>
        <button type="button" className={ERRBTN} onClick={() => setError(null)}>
          {COPY.discard}
        </button>
      </div>
    </div>
  );

  if (phase === 'open') {
    const mac = isMac();
    return (
      <Tag className={block ? 'block' : 'block w-full'}>
        <textarea
          ref={editorRef}
          rows={1}
          value={draft}
          onChange={onEditorChange}
          onKeyDown={onEditorKeyDown}
          onBlur={() => commit(false)}
          aria-label={COPY.editLabel(label)}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? msgId : undefined}
          enterKeyHint={multiline ? undefined : 'done'}
          data-testid={`${testIdBase}-editor`}
          className={`${EDITOR} ${invalid ? RING_INVALID : RING_OK}`}
          style={{ fieldSizing: 'content' } as React.CSSProperties}
        />
        {invalid && (
          <span
            id={msgId}
            className="block mt-1 text-[11px] leading-4 font-normal not-italic text-attention-on"
          >
            {COPY.emptyRequired(label)}
          </span>
        )}
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-4 font-normal not-italic text-inkMuted">
          <span className="[@media(pointer:coarse)]:hidden contents">
            {multiline ? (
              <span>
                <kbd className={KBD}>{mac ? '⌘' : 'Ctrl'}</kbd>
                <kbd className={KBD}>Enter</kbd> {COPY.keySave} · <kbd className={KBD}>Esc</kbd>{' '}
                {COPY.keyCancel} · {COPY.keyNewline}
              </span>
            ) : (
              <span>
                <kbd className={KBD}>Enter</kbd> {COPY.keySave} · <kbd className={KBD}>Esc</kbd>{' '}
                {COPY.keyCancel}
              </span>
            )}
          </span>
          <span className="ml-auto inline-flex items-center gap-1">
            <button
              type="button"
              className={BTN_CANCEL}
              onMouseDown={keepFocus}
              onClick={() => close(true)}
            >
              {COPY.cancel}
            </button>
            <button
              type="button"
              className={BTN_SAVE}
              onMouseDown={keepFocus}
              onClick={() => commit(true)}
            >
              {COPY.save}
            </button>
          </span>
        </span>
        {editorNote && <span className="block mt-1 text-[11px] text-inkMuted">{editorNote}</span>}
        {errorBox}
      </Tag>
    );
  }

  const runClass = `${block ? BLOCK : RUN}${saving ? ' cursor-progress' : ''}${
    empty && placeholder ? ` ${PLACEHOLDER}` : ''
  }${className ? ` ${className}` : ''}`;

  const run = (
    <Tag
      ref={runRef as React.Ref<HTMLSpanElement>}
      role="button"
      tabIndex={0}
      data-testid={testIdBase}
      aria-describedby={SR_OPEN_HINT_ID}
      aria-busy={saving || undefined}
      aria-disabled={saving || disabled || undefined}
      className={runClass}
      onClick={onRunClick}
      onKeyDown={onRunKeyDown}
    >
      {restContent}
    </Tag>
  );

  if (block) {
    return (
      <>
        {run}
        {status}
        {errorBox}
      </>
    );
  }
  return (
    <>
      {run}
      {status}
      {errorBox && <span className="block">{errorBox}</span>}
    </>
  );
};

export default InlineEditable;
